import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import type { MRIMachinePopulated } from '../room-overview/types';
import {
    getPublicMRIMachines,
    getAllMRIMachinesByDepartment,
    addMRIMachine,
    deleteMRIMachine,
} from '../../services/apiService';
import ScannerTemplatePicker from '../onboarding/ScannerTemplatePicker';
import ScannerForm from '../onboarding/ScannerForm';
import AddedScannersList from '../onboarding/AddedScannersList';

interface FacilityScannersSectionProps {
    onChange?: () => void;
}

export default function FacilityScannersSection({ onChange }: FacilityScannersSectionProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [templates, setTemplates] = useState<MRIMachinePopulated[]>([]);
    const [scanners, setScanners] = useState<MRIMachinePopulated[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedTemplate, setSelectedTemplate] =
        useState<MRIMachinePopulated | null>(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [publicMachines, departmentMachines] = await Promise.all([
                    getPublicMRIMachines(),
                    getAllMRIMachinesByDepartment(),
                ]);
                setTemplates(
                    Array.isArray(publicMachines)
                        ? publicMachines.filter(
                              (m: MRIMachinePopulated) => m?._id
                          )
                        : []
                );
                setScanners(
                    Array.isArray(departmentMachines)
                        ? departmentMachines
                        : []
                );
            } catch {
                toast({
                    title: t('facilities.scanners.error'),
                    description: t('facilities.scanners.toasts.loadFailed'),
                    variant: 'destructive',
                });
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAdd = async (name: string, coils: string) => {
        if (!selectedTemplate) return;

        setIsSaving(true);
        try {
            await addMRIMachine({
                Model: selectedTemplate.Model,
                Manufacturer: selectedTemplate.Manufacturer,
                Name: name,
                FieldStrength: selectedTemplate.FieldStrength,
                SpatialGradient: selectedTemplate.SpatialGradient || [],
                SlewRatePerAxis: selectedTemplate.SlewRatePerAxis,
                MaxSlewRate: selectedTemplate.MaxSlewRate,
                MaxSAR: selectedTemplate.MaxSAR || [],
                MaxB1rms: selectedTemplate.MaxB1rms,
                TRCoils: coils,
            });

            const updated = await getAllMRIMachinesByDepartment();
            setScanners(Array.isArray(updated) ? updated : []);
            setSelectedTemplate(null);
            setDialogOpen(false);
            toast({ title: t('facilities.scanners.toasts.added') });
            onChange?.();
        } catch {
            toast({
                title: t('facilities.scanners.error'),
                description: t('facilities.scanners.toasts.addFailed'),
                variant: 'destructive',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemove = async (machineId: string) => {
        try {
            await deleteMRIMachine(machineId);
            setScanners((prev) => prev.filter((m) => m._id !== machineId));
            onChange?.();
        } catch {
            toast({
                title: t('facilities.scanners.error'),
                description: t('facilities.scanners.toasts.removeFailed'),
                variant: 'destructive',
            });
        }
    };

    const handleOpenDialog = () => {
        setSelectedTemplate(null);
        setDialogOpen(true);
    };

    return (
        <section className="mt-10">
            <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="text-xl font-semibold text-foreground">
                            {t('facilities.scanners.title')}
                        </h3>
                        <span className="rounded-full bg-zinc-300 px-2 py-0.5 text-xs font-medium tabular-nums text-zinc-800">
                            {scanners.length}
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t('facilities.scanners.description')}
                    </p>
                </div>
                <Button size="sm" onClick={handleOpenDialog}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {t('facilities.scanners.addScanner')}
                </Button>
            </div>

            <AddedScannersList scanners={scanners} onRemove={handleRemove} showHeading={false} />

            <Dialog
                open={dialogOpen}
                onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) setSelectedTemplate(null);
                }}
            >
                <DialogContent className="max-h-[85vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{t('facilities.scanners.dialogTitle')}</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <ScannerTemplatePicker
                            templates={templates}
                            selectedId={selectedTemplate?._id ?? null}
                            onSelect={setSelectedTemplate}
                            onClear={() => setSelectedTemplate(null)}
                        />

                        {selectedTemplate && (
                            <ScannerForm
                                key={selectedTemplate._id}
                                template={selectedTemplate}
                                isSaving={isSaving}
                                onAdd={handleAdd}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
}
